"use client";
import { motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowRight,
  Building2,
  ClipboardCheck,
  Fingerprint,
  Globe,
  UserPlus,
} from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import useAutoRotate from "@/hooks/useAutoRotate";
import RichText from "./RichText";

const FEATURE_ICONS = [Fingerprint, UserPlus, Globe, ClipboardCheck, Building2];
const ROTATE_MS = 4000;

/* `num` and the screenshot are presentation, not copy, so they stay here and
   index-align with `showcase.features` in the dictionary. */
const FEATURE_NUMS = ["01", "02", "03", "04", "05"];

/**
 * The real product screen behind each of the five standout features.
 *
 * These replaced a hand-built dark "dashboard" — three invented rows and two
 * invented stats per feature, drawn in JSX inside a window frame. It looked
 * convincing, which was the problem: the one section of the landing page that
 * exists to prove these five capabilities are actually built was the one
 * showing a drawing of them. Each entry below is a capture of the screen the
 * copy beside it is describing, and it is now shown on its own — no frame, no
 * chrome, no stat cards — because every one of those was furniture taking
 * width and height off the only thing here worth looking at.
 *
 * Same two cuts as the /features hero shots (see the header of
 * `featureScreenshots.ts` for the full reasoning): x≥220 drops the app's own
 * nav rail, which this site deliberately does not publish, and the bottom drops
 * the "Copyright © 2026 - Maple Ridge Public School" band the admin shell pins
 * to the viewport. Both are baked into the file — a CSS crop still leaves the
 * whole image downloadable at its URL.
 *
 * 1220×700 rather than the 1220×854 /features uses, because this panel sits
 * beside a five-row list and has to finish at roughly the same height; 854
 * makes it overshoot, and the consent-form capture — a class with five forms in
 * it — is a third empty white space at that depth. The shape is in the filename
 * because the aspect ratio below is hard-coded to it and /screenshots is plain
 * static hosting with no content hash, so re-cutting one in place would serve a
 * cached copy of the old shape into a box declared as the new one. Never
 * overwrite one: change the name, and delete the file you replaced.
 */
const FEATURE_SHOTS = [
  "/screenshots/admin/payroll-run-1220x700.webp",
  "/screenshots/admin/admissions-1220x700.webp",
  "/screenshots/admin/website-content-1220x700.webp",
  "/screenshots/admin/consent-form-1220x700.webp",
  "/screenshots/admin/permissions-1220x700.webp",
];

type ShowcaseFeature = {
  title: string;
  subtitle: string;
  imageAlt: string;
};

export default function ProductShowcase() {
  const { t, tv } = useLanguage();
  const features = tv<ShowcaseFeature[]>("showcase.features");

  /* Auto-rotate the active feature. The timer used to be a bare
     `setInterval` gated on nothing but hover, so it kept firing — and kept
     re-rendering this whole block, transitions and all — for the entire
     visit, including all the time this section spent nowhere near the
     viewport. `useAutoRotate` stops it when the section is off screen, in a
     background tab, or the visitor has asked for reduced motion. */
  const {
    containerRef,
    index: activeIndex,
    setIndex: setActiveIndex,
    pause,
    resume,
    paused: isPaused,
  } = useAutoRotate(features.length, ROTATE_MS);

  return (
    <section
      ref={containerRef as React.RefObject<HTMLElement>}
      id="product-showcase"
      className="w-full scroll-mt-24 py-12 sm:py-16 lg:py-24 bg-[#FAFCFF] overflow-hidden relative"
    >
      {/* Subtle background glow, painted with radial-gradients rather than a
          `blur-3xl` filter over a solid disc. A filtered layer has to be
          rasterised into its own offscreen surface and run through a 64px
          Gaussian; a gradient is one ordinary paint. At 3-4% opacity the two
          are indistinguishable, so this is the same look for none of the
          cost. */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(24rem 24rem at 100% 0%, rgba(42,72,201,0.04), transparent 70%), radial-gradient(24rem 24rem at 0% 100%, rgba(20,184,166,0.03), transparent 70%)",
        }}
      />

      <div className="site-container relative z-10">

        <div
          onMouseEnter={pause}
          onMouseLeave={resume}
          className="relative"
        >

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center max-w-[720px] mx-auto mb-10 sm:mb-16"
          >
            <h2 className="font-heading section-heading text-[#0d1117] mb-3.5">
              <RichText text={t("showcase.heading")} />
            </h2>
            <p className="text-[14.5px] sm:text-[17px] text-[#596875] leading-[1.6] max-w-[560px] mx-auto">
              {t("showcase.body")}
            </p>
          </motion.div>

          {/* 2-Column Main Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
            
            {/* LEFT COLUMN: Vertical Timeline List (5 Items) */}
            <div className="lg:col-span-6 flex flex-col justify-between">
              
              <div className="relative space-y-2 sm:space-y-2.5">
                {features.map((m, idx) => {
                  const isActive = idx === activeIndex;
                  const Icon = FEATURE_ICONS[idx];
                  return (
                    <button
                      key={FEATURE_NUMS[idx]}
                      type="button"
                      onClick={() => setActiveIndex(idx)}
                      aria-current={isActive}
                      className={`group relative z-10 w-full text-left flex items-center gap-3.5 sm:gap-4
                                  p-3 sm:p-4 rounded-2xl cursor-pointer overflow-hidden
                                  transition-all duration-500 ease-out
                                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3D5EE1]/40 ${
                        isActive
                          ? "bg-white ring-1 ring-[#3D5EE1]/18 shadow-[0_1px_0_0_rgba(255,255,255,0.9)_inset,0_14px_30px_-16px_rgba(61,94,225,0.45)] translate-x-0 sm:translate-x-1.5"
                          : "bg-white/55 hover:bg-white/85 ring-1 ring-slate-900/[0.04] hover:ring-slate-900/[0.07] hover:translate-x-1"
                      }`}
                    >
                      {/* left accent bar */}
                      <span
                        aria-hidden
                        className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] rounded-r-full
                                    bg-gradient-to-b from-[#3D5EE1] to-brand-400
                                    transition-all duration-500 ease-out ${isActive ? "h-[62%] opacity-100" : "h-0 opacity-0"}`}
                      />

                      {/* Icon / number badge */}
                      <span
                        className={`relative w-11 h-11 sm:w-[52px] sm:h-[52px] rounded-[14px] sm:rounded-2xl
                                    flex items-center justify-center shrink-0
                                    transition-all duration-500 ease-out ${
                          isActive
                            ? "bg-[linear-gradient(180deg,#3D5EE1_0%,#3D5EE1_100%)] text-white shadow-[0_8px_18px_-8px_rgba(61,94,225,0.75)]"
                            : "bg-[#3D5EE1]/[0.07] text-[#3D5EE1] group-hover:bg-[#3D5EE1]/[0.11]"
                        }`}
                      >
                        <Icon size={19} strokeWidth={2} className="sm:w-[21px] sm:h-[21px]" />
                        <span
                          className={`absolute -top-1.5 -right-1.5 min-w-[19px] h-[19px] px-1 rounded-full
                                      flex items-center justify-center text-[9px] font-bold tabular-nums
                                      ring-2 ring-[#FAFCFF] transition-colors duration-500 ${
                            // slate-600, not slate-400. At 9px bold on white
                            // slate-400 (#94a3b8) measures 2.56:1 — well under
                            // the 4.5:1 WCAG AA needs for text this size, and
                            // the smallest type on the page is the last place
                            // that can afford to be faint. slate-600 (#475569)
                            // is 7.5:1 and still reads as the quiet state next
                            // to the near-black active pill.
                            isActive ? "bg-[#0d1117] text-white" : "bg-white text-slate-600 ring-[#FAFCFF]"
                          }`}
                        >
                          {FEATURE_NUMS[idx]}
                        </span>
                      </span>

                      {/* Text Details */}
                      <span className="flex-1 min-w-0">
                        <span
                          className={`block font-sora font-bold text-[13.5px] sm:text-[16px] leading-snug tracking-[-0.01em] transition-colors duration-300 ${
                            isActive ? "text-[#0d1117]" : "text-slate-700 group-hover:text-[#0d1117]"
                          }`}
                        >
                          {m.title}
                        </span>
                        <span className="block text-[11px] sm:text-[13px] text-slate-500 line-clamp-2 sm:line-clamp-1 mt-0.5 sm:mt-1">
                          {m.subtitle}
                        </span>
                      </span>

                      <ArrowRight
                        size={16}
                        className={`shrink-0 transition-all duration-500 ease-out ${
                          isActive
                            ? "text-[#3D5EE1] opacity-100 translate-x-0"
                            : "text-slate-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0"
                        }`}
                      />

                      {/* auto-rotate progress bar */}
                      {isActive && !isPaused && (
                        <motion.span
                          key={activeIndex}
                          aria-hidden
                          className="absolute bottom-0 left-0 h-[2px] bg-gradient-to-r from-[#3D5EE1] to-brand-400 rounded-full"
                          initial={{ width: "0%" }}
                          animate={{ width: "100%" }}
                          transition={{ duration: ROTATE_MS / 1000, ease: "linear" }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Action Buttons at Bottom Left */}
              <div className="flex flex-wrap items-center gap-3 pt-6 sm:pt-9">
                <Link
                  href="/features"
                  className="group inline-flex items-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 rounded-full
                             text-[13px] sm:text-[15px] font-sora font-bold tracking-[-0.01em] text-[#3D5EE1]
                             bg-white/70 hover:bg-white/90 backdrop-blur-md
                             ring-1 ring-[#3D5EE1]/15 hover:ring-[#3D5EE1]/25
                             shadow-[0_1px_0_0_rgba(255,255,255,0.9)_inset,0_6px_18px_-8px_rgba(15,23,42,0.22)]
                             transition-[transform,box-shadow,background-color] duration-300 ease-out hover:-translate-y-[3px]
                             active:translate-y-0 active:duration-100
                             focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-offset-2"
                >
                  <span>{t("showcase.exploreAll")}</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300 ease-out" />
                </Link>
              </div>

            </div>

            {/* RIGHT COLUMN: the product screen, and nothing wrapped around it */}
            <div className="lg:col-span-6 flex flex-col justify-center relative">
              {/* ambient glow behind the card */}
              <div aria-hidden className="absolute -inset-6 bg-[#3D5EE1]/[0.10] blur-3xl rounded-[48px] pointer-events-none" />

              {/* This used to be a dark app-window frame: traffic lights, a
                  breadcrumb, a LIVE badge, two stat cards and a footer bar, all
                  drawn around the capture. Between the chrome and its padding
                  that furniture was taking ~40px of width and ~150px of height
                  off the one thing the section exists to show, and rendering
                  the dashboard smaller than it had to be. The screenshot now
                  gets the whole column.

                  The container never remounts on rotation — it used to be keyed
                  on activeIndex inside AnimatePresence, which with an image
                  inside meant re-decoding on a timer and a flash of empty frame
                  between the two. The five captures are all mounted and swapped
                  by opacity instead.

                  They do NOT cross-fade: the outgoing one clears first and the
                  incoming one is held back until it has, because two dashboards
                  at half opacity read as a rendering fault rather than a
                  transition. The gap lands on this box's own white, which is
                  also the product's background, so it dissolves through the
                  page colour rather than flashing.

                  aspectRatio matches the files exactly and object-contain — not
                  cover — does the fitting, so a capture swapped for one of
                  another shape letterboxes rather than silently losing content
                  off its edges. */}
              <div
                className="relative w-full overflow-hidden bg-white
                           rounded-[18px] sm:rounded-[24px]
                           ring-1 ring-slate-900/[0.07]
                           shadow-[0_30px_70px_-30px_rgba(15,23,42,0.45)]"
                style={{ aspectRatio: "1220/700" }}
              >
                {features.map((m, idx) => (
                  <Image
                    key={FEATURE_NUMS[idx]}
                    src={FEATURE_SHOTS[idx]}
                    alt={m.imageAlt}
                    fill
                    /* Served as-is rather than through /_next/image, which is
                       the difference between a sharp screenshot and a soft one
                       here. These files are already exactly 1220 wide — the
                       full width of the capture minus the nav rail, and the
                       most detail that exists. The box draws them at ~656 CSS
                       px, so on a 2x display the optimizer is asked for
                       ~1312px, finds no candidate at that size, and re-encodes
                       the 1220 source UP to 1920. Upscaling then downscaling
                       an 11px table row is exactly how dashboard text goes
                       mushy, and it costs bandwidth to do it.

                       Skipping the optimizer hands the browser the real 1220
                       pixels and lets it do the one downscale itself. The five
                       together are 237KB, they sit below the fold, and they
                       are already WebP at q90 — one lossy pass from the raw
                       capture, where the optimizer would have added a second. */
                    unoptimized
                    aria-hidden={idx !== activeIndex}
                    className={`object-contain object-top transition-opacity ease-out ${
                      idx === activeIndex
                        ? "opacity-100 duration-300 delay-200"
                        : "opacity-0 duration-200 delay-0"
                    }`}
                  />
                ))}
              </div>
            </div>

          </div>

        </div>

      </div>

    </section>
  );
}

